import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext.jsx';
import BulkUploadModal from './customer/BulkUploadModal';

const Customer = () => {
  const { logout } = useContext(AuthContext);

  // Helper to get correct auth token
  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('authToken');
  };
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  
  // Available tags for the system
  const [availableTags, setAvailableTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    dobMonth: '',
    dobDay: '',
    businessUnits: [],
    tags: [],
    notes: ''
  });

  // Filter and pagination states
  const [filters, setFilters] = useState({
    search: '',
    businessUnits: '',
    tags: '',
    isActive: '',
    startDate: '',
    endDate: '',
    payingOnly: 'true',  // Default to showing only paying customers
    sortBy: 'totalSpent',  // Default sort by total spent
    sortOrder: 'desc'  // Highest spent first
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20  // Increased from 10 to 20
  });

  // Selected customers for bulk operations
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkTags, setBulkTags] = useState([]);
  
  // 🆕 PHASE 2: Bulk Upload Modal state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Business units options
  const businessUnitsOptions = ['Salon', 'Spa', 'Gym', 'Others'];

  // Month and day options for date of birth
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const getDaysInMonth = (month) => {
    if (!month) return 31;
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return daysInMonth[parseInt(month) - 1];
  };

  const days = useMemo(() => {
    const maxDays = getDaysInMonth(customerForm.dobMonth);
    return Array.from({ length: maxDays }, (_, i) => {
      const day = (i + 1).toString().padStart(2, '0');
      return { value: day, label: day };
    });
  }, [customerForm.dobMonth]);

  // Extract all unique tags from customers
  useEffect(() => {
    const allTags = new Set();
    customers.forEach(customer => {
      if (customer.tags && Array.isArray(customer.tags)) {
        customer.tags.forEach(tag => allTags.add(tag));
      }
    });
    setAvailableTags(Array.from(allTags).sort());
  }, [customers]);

  // OPTIMIZED: Single API call for both customers and stats
  const fetchCustomersAndStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const queryParams = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });

      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/customers?${queryParams}`);

      setCustomers(response.data.customers || []);
      setStats(response.data.stats || {});
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.pagination?.totalPages || 1,
        totalCount: response.data.pagination?.totalCount || 0
      }));
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (error.response?.status === 401) {
        logout();
        window.location.href = '/login';
      } else {
        setError(error.response?.data?.message || 'Failed to fetch customers. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, filters]);

  // Load data on mount and filter changes
  useEffect(() => {
    fetchCustomersAndStats();
  }, [fetchCustomersAndStats]);

  // Handle form input changes
  const handleFormChange = useCallback((field, value) => {
    setCustomerForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // Update dateOfBirth when month or day changes
    if (field === 'dobMonth' || field === 'dobDay') {
      const month = field === 'dobMonth' ? value : customerForm.dobMonth;
      const day = field === 'dobDay' ? value : customerForm.dobDay;
      
      if (month && day) {
        setCustomerForm(prev => ({
          ...prev,
          dateOfBirth: `${month}-${day}`
        }));
      }
    }
  }, [customerForm.dobMonth, customerForm.dobDay, validationErrors]);

  // Handle tags input
  const handleAddTag = (tag) => {
    if (tag && !customerForm.tags.includes(tag)) {
      setCustomerForm(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setCustomerForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle business units selection
  const handleBusinessUnitToggle = (unit) => {
    setCustomerForm(prev => {
      const newUnits = prev.businessUnits.includes(unit)
        ? prev.businessUnits.filter(u => u !== unit)
        : [...prev.businessUnits, unit];
      return { ...prev, businessUnits: newUnits };
    });
  };

  // Handle filter changes
  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, []);

  // Client-side validation
  const validateForm = () => {
    const errors = {};
    
    if (!customerForm.name.trim()) {
      errors.name = 'Customer name is required';
    }
    
    if (customerForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerForm.email)) {
      errors.email = 'Please enter a valid email address (e.g., customer@example.com)';
    }
    
    if (customerForm.phone && !/^\+?\d{10,15}$/.test(customerForm.phone)) {
      errors.phone = 'Please enter a valid phone number (10-15 digits, e.g., +2348012345678)';
    }
    
    // Validate date of birth if provided
    if (customerForm.dobMonth && !customerForm.dobDay) {
      errors.dateOfBirth = 'Please select both month and day';
    } else if (!customerForm.dobMonth && customerForm.dobDay) {
      errors.dateOfBirth = 'Please select both month and day';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // OPTIMIZED: Submit customer form
  const handleSubmitCustomer = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      setError('Please correct the errors in the form');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setValidationErrors({});
      
      const url = editingCustomer 
        ? `${import.meta.env.VITE_API_URL}/api/customers/${editingCustomer._id}`
        : `${import.meta.env.VITE_API_URL}/api/customers`;
      
      const method = editingCustomer ? 'put' : 'post';

      const formData = {
        name: customerForm.name,
        email: customerForm.email || undefined,
        phone: customerForm.phone || undefined,
        dateOfBirth: customerForm.dateOfBirth || undefined,
        businessUnits: customerForm.businessUnits,
        tags: customerForm.tags,
        notes: customerForm.notes || undefined
      };

      await axios[method](url, formData);
      await fetchCustomersAndStats();
      resetForm();
    } catch (error) {
      console.error('Error saving customer:', error);
      
      if (error.response?.data?.errors) {
        const backendErrors = {};
        error.response.data.errors.forEach(err => {
          backendErrors[err.field] = err.message;
        });
        setValidationErrors(backendErrors);
      }
      
      setError(error.response?.data?.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCustomerForm({
      name: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      dobMonth: '',
      dobDay: '',
      businessUnits: [],
      tags: [],
      notes: ''
    });
    setNewTag('');
    setEditingCustomer(null);
    setShowAddForm(false);
    setValidationErrors({});
    setError('');
  };

  // Edit customer
  const handleEditCustomer = (customer) => {
    const [month, day] = (customer.dateOfBirth || '').split('-');
    
    setCustomerForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      dateOfBirth: customer.dateOfBirth || '',
      dobMonth: month || '',
      dobDay: day || '',
      businessUnits: customer.businessUnits || [],
      tags: customer.tags || [],
      notes: customer.notes || ''
    });
    setEditingCustomer(customer);
    setShowAddForm(true);
  };

  // Delete customer
  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      setLoading(true);
      await axios.delete(`${import.meta.env.VITE_API_URL}/api/customers/${customerId}`);
      await fetchCustomersAndStats();
    } catch (error) {
      console.error('Error deleting customer:', error);
      setError(error.response?.data?.message || 'Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  // Handle customer selection
  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      }
      return [...prev, customerId];
    });
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(c => c._id));
    }
  };

  // Bulk operations
  const handleBulkAction = async (action) => {
    if (selectedCustomers.length === 0) {
      setError('No customers selected');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (action === 'delete') {
        if (!window.confirm(`Delete ${selectedCustomers.length} customer(s)?`)) return;
        
        await axios.post(`${import.meta.env.VITE_API_URL}/api/customers/bulk`, {
          action: 'delete',
          customerIds: selectedCustomers
        });
      } else if (action === 'addTags') {
        if (bulkTags.length === 0) {
          setError('Please select tags to add');
          return;
        }

        await axios.post(`${import.meta.env.VITE_API_URL}/api/customers/bulk`, {
          action: 'update',
          customerIds: selectedCustomers,
          data: {
            $addToSet: { tags: { $each: bulkTags } }
          }
        });
      } else if (action === 'removeTags') {
        if (bulkTags.length === 0) {
          setError('Please select tags to remove');
          return;
        }

        await axios.post(`${import.meta.env.VITE_API_URL}/api/customers/bulk`, {
          action: 'update',
          customerIds: selectedCustomers,
          data: {
            $pullAll: { tags: bulkTags }
          }
        });
      }

      setSelectedCustomers([]);
      setShowBulkActions(false);
      setBulkTags([]);
      await fetchCustomersAndStats();
    } catch (error) {
      console.error('Error in bulk operation:', error);
      setError(error.response?.data?.message || 'Bulk operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Export functionality
  const handleExport = async (format) => {
    try {
      const queryParams = new URLSearchParams({
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/customers/export/${format}?${queryParams}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting customers:', error);
      setError('Failed to export customers');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Customer Management</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your customer database and relationships</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Customers</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">
              {stats.totalCustomers || 0}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-emerald-200 bg-emerald-50">
            <div className="text-xs sm:text-sm text-emerald-700 mb-1">Active Clients</div>
            <div className="text-xl sm:text-2xl font-bold text-emerald-600">
              {stats.payingCustomers || 0}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-xs sm:text-sm text-gray-600 mb-1">Salon</div>
            <div className="text-xl sm:text-2xl font-bold text-pink-600">
              {stats.salonCustomers || 0}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-xs sm:text-sm text-gray-600 mb-1">Spa</div>
            <div className="text-xl sm:text-2xl font-bold text-blue-600">
              {stats.spaCustomers || 0}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-xs sm:text-sm text-gray-600 mb-1">Gym</div>
            <div className="text-xl sm:text-2xl font-bold text-green-600">
              {stats.gymCustomers || 0}
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {/* Search */}
            <input
              type="text"
              placeholder="Search customers..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
            />

            {/* Business Units Filter */}
            <select
              value={filters.businessUnits}
              onChange={(e) => handleFilterChange('businessUnits', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
            >
              <option value="">All Business Units</option>
              {businessUnitsOptions.map(unit => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>

            {/* Tags Filter */}
            <select
              value={filters.tags}
              onChange={(e) => handleFilterChange('tags', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
            >
              <option value="">All Tags</option>
              {availableTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>

            {/* Active Status Filter */}
            <select
              value={filters.isActive}
              onChange={(e) => handleFilterChange('isActive', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          {/* Second row of filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {/* Paying Customers Filter */}
            <select
              value={filters.payingOnly}
              onChange={(e) => handleFilterChange('payingOnly', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
            >
              <option value="true">Active Clients Only</option>
              <option value="false">All Customers</option>
            </select>

            {/* Sort By */}
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
            >
              <option value="totalSpent">Sort by: Total Spent</option>
              <option value="visitCount">Sort by: Visit Count</option>
              <option value="createdAt">Sort by: Date Added</option>
              <option value="name">Sort by: Name</option>
            </select>

            {/* Sort Order */}
            <button
              onClick={() => handleFilterChange('sortOrder', filters.sortOrder === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center justify-center gap-2"
            >
              {filters.sortOrder === 'desc' ? (
                <>
                  <span>↓</span> Highest First
                </>
              ) : (
                <>
                  <span>↑</span> Lowest First
                </>
              )}
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 text-sm font-medium"
            >
              + Add Customer
            </button>
            
            {/* 🆕 PHASE 2: Bulk Upload Button */}
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium flex items-center gap-1"
            >
              📤 Bulk Upload
            </button>
            
            {selectedCustomers.length > 0 && (
              <>
                <button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Bulk Actions ({selectedCustomers.length})
                </button>
                <button
                  onClick={() => setSelectedCustomers([])}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm font-medium"
                >
                  Clear Selection
                </button>
              </>
            )}
            
            <button
              onClick={() => handleExport('csv')}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
            >
              Export CSV
            </button>
            <button
              onClick={() => handleExport('excel')}
              className="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 text-sm font-medium"
            >
              Export Excel
            </button>
          </div>

          {/* Bulk Actions Panel */}
          {showBulkActions && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold mb-3">Bulk Actions</h4>
              <div className="space-y-3">
                {/* Bulk Tag Management */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Manage Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {availableTags.map(tag => (
                      <label key={tag} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={bulkTags.includes(tag)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBulkTags(prev => [...prev, tag]);
                            } else {
                              setBulkTags(prev => prev.filter(t => t !== tag));
                            }
                          }}
                          className="mr-1"
                        />
                        <span className="text-xs">{tag}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleBulkAction('addTags')}
                      disabled={bulkTags.length === 0}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                    >
                      Add Tags
                    </button>
                    <button
                      onClick={() => handleBulkAction('removeTags')}
                      disabled={bulkTags.length === 0}
                      className="px-3 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 disabled:opacity-50"
                    >
                      Remove Tags
                    </button>
                  </div>
                </div>

                {/* Bulk Delete */}
                <div className="pt-3 border-t border-gray-200">
                  <button
                    onClick={() => handleBulkAction('delete')}
                    className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                  >
                    Delete Selected
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading && customers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Loading customers...</div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No customers found. Add your first customer to get started!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.length === customers.length && customers.length > 0}
                        onChange={handleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Units
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tags
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visits
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Spent
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customers.map((customer) => (
                    <tr key={customer._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedCustomers.includes(customer._id)}
                          onChange={() => handleSelectCustomer(customer._id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                        {customer.dateOfBirth && (
                          <div className="text-xs text-gray-500">DOB: {customer.dateOfBirth}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{customer.email || '-'}</div>
                        <div className="text-xs text-gray-500">{customer.phone || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {customer.businessUnits && customer.businessUnits.length > 0 ? (
                            customer.businessUnits.map((unit, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                              >
                                {unit}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {customer.tags && customer.tags.length > 0 ? (
                            customer.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">No tags</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.visitCount || 0}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-sm font-semibold text-emerald-600">
                          ₦{(customer.totalSpent || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEditCustomer(customer)}
                            className="text-blue-600 hover:text-blue-900 text-xs font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteCustomer(customer._id)}
                            className="text-red-600 hover:text-red-900 text-xs font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-xs sm:text-sm text-gray-700">
                    Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of{' '}
                    {pagination.totalCount} customers
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                      disabled={pagination.currentPage === 1}
                      className="px-3 py-2 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    
                    {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: pageNum }))}
                          className={`px-3 py-2 border rounded text-xs sm:text-sm ${
                            pagination.currentPage === pageNum
                              ? 'bg-pink-600 text-white border-pink-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="px-3 py-2 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Customer Modal - FIXED: Overflow issue */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 overflow-y-auto">
            <div className="bg-white rounded-lg w-full max-w-2xl my-8 max-h-[90vh] flex flex-col">
              <div className="p-4 sm:p-6 overflow-y-auto">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                  {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                </h3>
                
                <form onSubmit={handleSubmitCustomer} className="space-y-4">
                  {/* Name Field */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerForm.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm ${
                        validationErrors.name 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-pink-500'
                      }`}
                    />
                    {validationErrors.name && (
                      <p className="mt-1 text-xs text-red-600">{validationErrors.name}</p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={customerForm.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      placeholder="customer@example.com"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm ${
                        validationErrors.email 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-pink-500'
                      }`}
                    />
                    {validationErrors.email && (
                      <p className="mt-1 text-xs text-red-600">{validationErrors.email}</p>
                    )}
                  </div>

                  {/* Phone Field */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={customerForm.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      placeholder="+2348123456789"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm ${
                        validationErrors.phone 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-pink-500'
                      }`}
                    />
                    {validationErrors.phone && (
                      <p className="mt-1 text-xs text-red-600">{validationErrors.phone}</p>
                    )}
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <select
                          value={customerForm.dobMonth}
                          onChange={(e) => handleFormChange('dobMonth', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm ${
                            validationErrors.dateOfBirth 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-pink-500'
                          }`}
                        >
                          <option value="">Month</option>
                          {months.map(month => (
                            <option key={month.value} value={month.value}>
                              {month.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <select
                          value={customerForm.dobDay}
                          onChange={(e) => handleFormChange('dobDay', e.target.value)}
                          disabled={!customerForm.dobMonth}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm ${
                            validationErrors.dateOfBirth 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-pink-500'
                          } ${!customerForm.dobMonth ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        >
                          <option value="">Day</option>
                          {days.map(day => (
                            <option key={day.value} value={day.value}>
                              {day.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {validationErrors.dateOfBirth && (
                      <p className="mt-1 text-xs text-red-600">{validationErrors.dateOfBirth}</p>
                    )}
                  </div>

                  {/* Business Units */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Business Units
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {businessUnitsOptions.map(unit => (
                        <label key={unit} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={customerForm.businessUnits.includes(unit)}
                            onChange={() => handleBusinessUnitToggle(unit)}
                            className="mr-2"
                          />
                          <span className="text-sm">{unit}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    
                    {/* Display current tags */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {customerForm.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-2 text-blue-600 hover:text-blue-900"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>

                    {/* Add new tag */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag(newTag);
                          }
                        }}
                        placeholder="Add a tag..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => handleAddTag(newTag)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                      >
                        Add
                      </button>
                    </div>

                    {/* Quick tag suggestions from available tags */}
                    {availableTags.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Quick add:</p>
                        <div className="flex flex-wrap gap-1">
                          {availableTags
                            .filter(tag => !customerForm.tags.includes(tag))
                            .slice(0, 10)
                            .map((tag, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleAddTag(tag)}
                                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                              >
                                + {tag}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes Field */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={customerForm.notes}
                      onChange={(e) => handleFormChange('notes', e.target.value)}
                      rows="3"
                      placeholder="Special preferences, allergies, etc."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
                    />
                  </div>

                  {/* Form Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {loading ? 'Saving...' : (editingCustomer ? 'Update Customer' : 'Add Customer')}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        
        {/* 🆕 PHASE 2: Bulk Upload Modal */}
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onUploadSuccess={(results) => {
            console.log('Bulk upload success:', results);
            // Refresh customer list
            fetchCustomersAndStats();
          }}
        />
      </div>
    </div>
  );
};

export default Customer;